<script setup lang="ts">
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import { DEFAULT_TIMEZONE, findTimezoneOption } from "~/utils/helpers/timezones";
import { buildTimezoneOptions } from "~/utils/helpers/timezone-labels";
import type { ITimezoneOption } from "~/utils/interfaces/timezones-interfaces";
import { useAuthStore } from "~/store/auth/auth.store";

/**
 * `v-model` is the IANA id, what the step stores. The labels are already translated, so they are marked
 * `notranslate`: Localize must neither translate them again nor save them as phrases.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: string | null;
    /** The step's start date, so the offsets are those of that day, summer time included. */
    referenceDate?: string | Date | null;
    error?: boolean;
    disabled?: boolean;
  }>(),
  { modelValue: null, referenceDate: null, error: false, disabled: false },
);

const emit = defineEmits<{ "update:modelValue": [value: string | null] }>();

const authStore = useAuthStore();

const options = computed<ITimezoneOption[]>(() => buildTimezoneOptions(props.referenceDate, authStore.user?.language));

/** A step without a zone reads as Paris, so the field is never empty and can't be cleared. */
const selected = computed({
  get(): ITimezoneOption | null {
    return findTimezoneOption(options.value, props.modelValue ?? DEFAULT_TIMEZONE);
  },
  set(option: ITimezoneOption | null) {
    emit("update:modelValue", option?.value ?? null);
  },
});
</script>

<template>
  <!-- The search runs on `searchText`; the slots display the label alone, wrapped rather than cut in the list. -->
  <SimpleDropdown
    v-model="selected"
    :options="options"
    label="searchText"
    track-by="value"
    searchable
    :delete="false"
    :error="props.error"
    :disabled="props.disabled"
    placeholder="Rechercher un fuseau"
  >
    <template #singleLabel="{ option }">
      <span class="notranslate">{{ option.label }}</span>
    </template>
    <template #option="{ option }">
      <span class="notranslate block whitespace-normal">{{ option.label }}</span>
    </template>
  </SimpleDropdown>
</template>
